"use client"

import * as React from "react"
import { Tooltip as TooltipPrimitive } from "radix-ui"

import { cn } from '@/shared/lib/utils';

function TooltipProvider({
    delayDuration = 0,
    disableHoverableContent = true,
    ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
    return (
        <TooltipPrimitive.Provider
            data-slot="tooltip-provider"
            delayDuration={delayDuration}
            disableHoverableContent={disableHoverableContent}
            {...props}
        />
    )
}

function Tooltip({
    ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
    return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({
    ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
    return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
    className,
    sideOffset = 0,
    children,
    hideArrow = false,
    arrowClassName,
    style,
    interactive = false,
    ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & { hideArrow?: boolean, arrowClassName?: string, interactive?: boolean }) {
    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
                data-slot="tooltip-content"
                sideOffset={sideOffset}
                style={{ zIndex: 10005, pointerEvents: interactive ? 'auto' : 'none', ...style }}
                className={cn(
                    "z-10005 inline-flex w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin) items-center gap-1.5 rounded-md bg-black px-3 py-1.5 text-xs text-white/80 has-data-[slot=kbd]:pr-1.5 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-[state=delayed-open]:slide-in-from-bottom-1 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-open:slide-in-from-bottom-1 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-closed:slide-out-to-bottom-1",
                    !interactive && "pointer-events-none",
                    className
                )}
                {...props}
            >
                {children}
                {hideArrow ? null : (
                    <TooltipPrimitive.Arrow className={cn("z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-black fill-black", arrowClassName)} />
                )}
            </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
    )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, Tip }

/**
 * 
 * interactive 默认值不能改为 true ，要用 false，要响应事件，再传入 true
 * false 有一个好处是，tip 不会残留显示和卡手问题，hover 移开即消失
 * 如果设置 true，则需要处理移入移出事件，否则会卡手
 * 
 */

function Tip({
    content,
    side = 'top',
    align = 'center',
    sideOffset = 0,
    alignOffset = 0,
    collisionPadding = 8,
    delayDuration = 0,
    className,
    hideArrow = false,
    arrowClassName,
    style,
    open,
    defaultOpen,
    onOpenChange,
    interactive = false,
    children,
}: {
    content: React.ReactNode
    side?: 'top' | 'bottom' | 'left' | 'right'
    align?: 'start' | 'center' | 'end'
    sideOffset?: number
    alignOffset?: number
    collisionPadding?: number | Partial<Record<'top' | 'right' | 'bottom' | 'left', number>>
    delayDuration?: number
    className?: string
    hideArrow?: boolean
    arrowClassName?: string
    style?: React.CSSProperties
    open?: boolean
    defaultOpen?: boolean
    onOpenChange?: (open: boolean) => void
    interactive?: boolean
    children: React.ReactNode
}) {
    return (
        <TooltipProvider delayDuration={delayDuration} disableHoverableContent={!interactive}>
            <Tooltip open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
                <TooltipTrigger asChild>
                    {children}
                </TooltipTrigger>
                <TooltipContent
                    side={side}
                    align={align}
                    sideOffset={sideOffset}
                    alignOffset={alignOffset}
                    collisionPadding={collisionPadding}
                    className={className}
                    hideArrow={hideArrow}
                    arrowClassName={arrowClassName}
                    style={style}
                    interactive={interactive}
                >
                    {content}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}
